import React, { useState } from 'react'
import {
  Box,
  Button,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { DatePicker, DatePickerProps, DateRange } from '@mui/x-date-pickers-pro'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import moment, { Moment } from 'moment'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { updateOneInArray } from '../../utils/utils'

interface DateRangeFieldDaysOfWeek {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

export interface DateRangeField {
  id: string
  dateRange: DateRange<Moment>
  daysOfWeek: DateRangeFieldDaysOfWeek
}

interface Props {
  dateRanges: DateRangeField[]
  onChange: (newDateRanges: DateRangeField[]) => void
  error?: string
  onDeleteDateRange?: (index: number, id: string) => void
  onAddDateRange?: (dateRange: DateRangeField) => void
  selectorSideRenderer?: (
    dateRangeField: DateRangeField,
    index: number
  ) => JSX.Element
}

const DateRangePickersWithWeekDays2: ReactFC<Props> = ({
  dateRanges,
  onChange,
  error,
  onDeleteDateRange,
  onAddDateRange,
  selectorSideRenderer
}) => {
  const { t } = useTranslation()

  const daysOfWeekTranslations: {
    [key in keyof DateRangeFieldDaysOfWeek]: string
  } = {
    monday: t('apps.rates.rateDataGrid.modal.monday'),
    tuesday: t('apps.rates.rateDataGrid.modal.tuesday'),
    wednesday: t('apps.rates.rateDataGrid.modal.wednesday'),
    thursday: t('apps.rates.rateDataGrid.modal.thursday'),
    friday: t('apps.rates.rateDataGrid.modal.friday'),
    saturday: t('apps.rates.rateDataGrid.modal.saturday'),
    sunday: t('apps.rates.rateDataGrid.modal.sunday')
  }

  const handleAddDateRange = () => {
    const newDateRange: DateRangeField = {
      id: v4(),
      dateRange: [moment(), moment().add(14, 'days')],
      daysOfWeek: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
      }
    }
    onChange([...dateRanges, newDateRange])
    onAddDateRange?.(newDateRange)
  }

  const handleDeleteDateRange = (index: number, id: string) => {
    onDeleteDateRange?.(index, id)
    onChange(dateRanges.filter((dateRange) => dateRange.id !== id))
  }

  const handleChangeDateRangeDates = (
    id: string,
    dateRange: DateRange<Moment>
  ) => {
    const dateRangeIndexToUpdate = dateRanges.findIndex(
      (currentDateRange) => currentDateRange.id === id
    )
    const newDateRanges = updateOneInArray(
      dateRanges,
      dateRangeIndexToUpdate,
      (dateRangeToUpdate) => {
        return {
          ...dateRangeToUpdate,
          dateRange
        }
      }
    )
    onChange(newDateRanges)
  }

  const handleChangeDayOfWeek = (
    id: string,
    dayOfWeek: keyof DateRangeFieldDaysOfWeek,
    value: boolean
  ) => {
    const dateRangeIndexToUpdate = dateRanges.findIndex(
      (currentDateRange) => currentDateRange.id === id
    )
    const newDateRanges = updateOneInArray(
      dateRanges,
      dateRangeIndexToUpdate,
      (dateRangeToUpdate) => {
        return {
          ...dateRangeToUpdate,
          daysOfWeek: {
            ...dateRangeToUpdate.daysOfWeek,
            [dayOfWeek]: value
          }
        }
      }
    )
    onChange(newDateRanges)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}
    >
      {error && (
        <Typography
          sx={{
            fontSize: '14px',
            color: 'red'
          }}
        >
          {error}
        </Typography>
      )}
      {dateRanges.map((dateRangeField, index) => {
        return (
          <Box
            key={dateRangeField.id}
            sx={{
              padding: '1rem',
              border: ({ palette }) => `1.5px solid ${palette.border.main}`,
              borderRadius: 2
            }}
          >
            <>
              {selectorSideRenderer?.(dateRangeField, index)}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <IconButton
                    onClick={() =>
                      handleDeleteDateRange(index, dateRangeField.id)
                    }
                  >
                    <HighlightOffIcon />
                  </IconButton>
                  <Box
                    sx={{
                      width: '250px',
                      display: 'flex',
                      gap: '.5rem'
                    }}
                  >
                    <DatePickerClicky
                      value={dateRangeField.dateRange[0]}
                      onChange={(date) => {
                        handleChangeDateRangeDates(dateRangeField.id, [
                          date,
                          dateRangeField.dateRange[1]
                        ])
                      }}
                    />
                    <DatePickerClicky
                      value={dateRangeField.dateRange[1]}
                      onChange={(date) => {
                        handleChangeDateRangeDates(dateRangeField.id, [
                          dateRangeField.dateRange[0],
                          date
                        ])
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    position: 'relative'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex'
                    }}
                  >
                    {Object.entries(daysOfWeekTranslations).map(
                      (objectEntry) => {
                        const [day, translation] = objectEntry as [
                          day: keyof DateRangeFieldDaysOfWeek,
                          translation: string
                        ]
                        // const checked = checkedDaysOfWeek?.includes(day);
                        return (
                          <FormControlLabel
                            key={`${dateRangeField.id}-${day}checkbox`}
                            label={translation.slice(0, 3)}
                            control={
                              <Checkbox
                                sx={{
                                  padding: '0.25rem'
                                }}
                                checked={dateRangeField.daysOfWeek[day]}
                                onChange={(e) => {
                                  handleChangeDayOfWeek(
                                    dateRangeField.id,
                                    day,
                                    e.target.checked
                                  )
                                }}
                              />
                            }
                          />
                        )
                      }
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          </Box>
        )
      })}
      <Button
        onClick={handleAddDateRange}
        sx={{
          width: 'max-content'
        }}
      >
        <AddCircleOutlineIcon
          sx={{
            marginRight: '0.5rem'
          }}
        />
        <Typography>
          {t('apps.rates.rateDataGrid.modal.add_date_range')}
        </Typography>
      </Button>
    </Box>
  )
}

const DatePickerClicky = (props: DatePickerProps<Moment>) => {
  const [open, setOpen] = useState(false)

  return (
    <DatePicker
      open={open}
      onClose={() => setOpen(false)}
      slots={{
        inputAdornment: () => <div></div>
      }}
      views={['year', 'month', 'day']}
      slotProps={{
        textField: {
          onClick: () => setOpen(true)
        },
        popper: {
          placement: 'bottom',
          disablePortal: true
        }
      }}
      minDate={moment()}
      {...props}
    />
  )
}

export default DateRangePickersWithWeekDays2
